import { Injectable, signal } from '@angular/core';
import { MenuSection, MenuItem, HybridMenuItem } from '../interfaces/menu.interface';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private menuData = signal<MenuSection[]>([
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: 'speedometer-outline',
      path: 'home',
      items: [],
    },
    {
      id: 'client',
      title: 'Client Management',
      icon: 'people-outline',
      items: [
        {
          id: 'clients',
          title: 'Clients',
          path: 'clients',
          icon: 'person-outline',
        },
      ],
    },
    {
      id: 'project',
      title: 'Project Management',
      icon: 'briefcase-outline',
      items: [
        {
          id: 'projects',
          title: 'Projects',
          path: 'projects',
          icon: 'folder-outline',
        },
        {
          id: 'products',
          title: 'Products',
          path: 'products',
          icon: 'cube-outline',
        },
      ],
    },
    {
      id: 'store',
      title: 'Store Management',
      icon: 'storefront-outline',
      items: [
        {
          id: 'stores',
          title: 'Stores',
          path: 'stores',
          icon: 'storefront-outline',
        },
        {
          id: 'store-types',
          title: 'Store Types',
          path: 'store-types',
          icon: 'list-outline',
        },
        {
          id: 'store-channels',
          title: 'Store Channels',
          path: 'store-channels',
          icon: 'git-branch-outline',
        },
        {
          id: 'store-sub-channels',
          title: 'Store Sub Channels',
          path: 'store-sub-channels',
          icon: 'git-merge-outline',
        },
      ],
    },
    {
      id: 'vendor',
      title: 'Vendor Management',
      icon: 'business-outline',
      items: [
        {
          id: 'vendors',
          title: 'Vendors',
          path: 'vendors',
          icon: 'business-outline',
        },
      ],
    },
    {
      id: 'order',
      title: 'Order Management',
      icon: 'cart-outline',
      items: [
        {
          id: 'order-types',
          title: 'Order Types',
          path: 'order-types',
          icon: 'list-outline',
        },
        {
          id: 'orders',
          title: 'Orders',
          path: 'orders',
          icon: 'cart-outline',
        },
        {
          id: 'public-markets',
          title: 'Public Markets',
          path: 'public-markets',
          icon: 'business-outline',
        },
        {
          id: 'market-stalls',
          title: 'Market Stalls',
          path: 'orders/market-stalls',
          icon: 'storefront-outline',
        },
      ],
    },
    {
      id: 'user',
      title: 'User Management',
      icon: 'people-outline',
      items: [
        {
          id: 'groups',
          title: 'Groups',
          path: 'groups',
          icon: 'people-circle-outline',
        },
        {
          id: 'users',
          title: 'Users',
          path: 'users',
          icon: 'person-outline',
        },
      ],
    },
  ]);

  private userMenuData = signal<MenuSection[]>([
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: 'speedometer-outline',
      items: [
        {
          id: 'home',
          title: 'Dashboard',
          path: 'home',
          icon: 'speedometer-outline',
        },
      ],
    },
    {
      id: 'order',
      title: 'Order Management',
      icon: 'cart-outline',
      items: [
        {
          id: 'orders',
          title: 'Orders',
          path: 'orders',
          icon: 'cart-outline',
        },
      ],
    },
  ]);

  private userClientMenuData = signal<MenuSection[]>([
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: 'speedometer-outline',
      items: [
        {
          id: 'home',
          title: 'Dashboard',
          path: 'home',
          icon: 'speedometer-outline',
        },
      ],
    },
    {
      id: 'order',
      title: 'Order Management',
      icon: 'cart-outline',
      items: [
        {
          id: 'public-markets',
          title: 'Public Markets',
          path: 'public-markets',
          icon: 'business-outline',
        },
        {
          id: 'market-stalls',
          title: 'Market Stalls',
          path: 'orders/market-stalls',
          icon: 'storefront-outline',
        },
      ],
    },
  ]);

  getMenuData() {
    return this.menuData;
  }

  getUserMenuData() {
    return this.userMenuData;
  }

  getUserClientMenuData() {
    return this.userClientMenuData;
  }

  getFlatMenuData() {
    // Create a flat menu structure without sections
    const flatMenuItems: MenuItem[] = [];

    // Extract all items from sections and add them to the flat list
    this.menuData().forEach((section) => {
      section.items.forEach((item) => {
        flatMenuItems.push({
          ...item,
          // Optionally add section info to the item if needed
          title: item.title,
        });
      });
    });

    return signal(flatMenuItems);
  }

  getFlatUserMenuData() {
    // Create a flat menu structure for user menu
    const flatMenuItems: MenuItem[] = [];

    this.userMenuData().forEach((section) => {
      section.items.forEach((item) => {
        flatMenuItems.push({
          ...item,
          title: item.title,
        });
      });
    });

    return signal(flatMenuItems);
  }

  getFlatUserClientMenuData() {
    // Create a flat menu structure for user client menu
    const flatMenuItems: MenuItem[] = [];

    this.userClientMenuData().forEach((section) => {
      section.items.forEach((item) => {
        flatMenuItems.push({
          ...item,
          title: item.title,
        });
      });
    });

    return signal(flatMenuItems);
  }

  /**
   * Transforms regular menu sections to hybrid menu format
   * Single item sections become direct menu items, multi-item sections stay as sections
   */
  private transformToHybridMenu(sections: MenuSection[]): HybridMenuItem[] {
    return sections.map(section => {
      // If section has only one item, return it as a single item
      if (section.items.length === 1) {
        return {
          type: 'single',
          ...section.items[0]
        } as HybridMenuItem;
      } 
      // Otherwise, return it as a section with items
      else {
        return {
          type: 'section',
          id: section.id,
          title: section.title,
          icon: section.icon,
          items: section.items,
          roles: section.roles,
          isExpanded: section.isExpanded
        } as HybridMenuItem;
      }
    });
  }

  /**
   * Returns hybrid menu data for admin users
   */
  getHybridMenuData() {
    return signal<HybridMenuItem[]>(this.transformToHybridMenu(this.menuData()));
  }

  /**
   * Returns hybrid menu data for regular users
   */
  getHybridUserMenuData() {
    return signal<HybridMenuItem[]>(this.transformToHybridMenu(this.userMenuData()));
  }

  /**
   * Returns hybrid menu data for client users
   */
  getHybridUserClientMenuData() {
    return signal<HybridMenuItem[]>(this.transformToHybridMenu(this.userClientMenuData()));
  }

  filterMenuByRoles(roles: string[]) {
    const filteredMenu = this.menuData()
      .map((section) => {
        if (
          section.roles &&
          !section.roles.some((role) => roles.includes(role))
        ) {
          return null;
        }

        const filteredItems = section.items.filter((item) => {
          if (!item.roles) return true;
          return item.roles.some((role) => roles.includes(role));
        });

        return filteredItems.length
          ? { ...section, items: filteredItems }
          : null;
      })
      .filter(Boolean) as MenuSection[];

    this.menuData.set(filteredMenu);
  }
}
